import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from './format';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
};

const variants: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white shadow-[0_1px_0_color-mix(in_srgb,var(--brand)_25%,transparent)]',
  secondary:
    'bg-[var(--bg-panel)] hover:bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text)]',
  ghost: 'bg-transparent hover:bg-[var(--bg-hover)] text-[var(--text-muted)]',
  danger: 'bg-[var(--danger)]/20 hover:bg-[var(--danger)]/30 text-[var(--danger)]',
};

const sizes: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'h-8 px-3 text-sm rounded-lg',
  md: 'h-10 px-4 text-sm rounded-[var(--radius-sm)]',
  lg: 'h-11 px-5 text-base rounded-[var(--radius)]',
};

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
